// Date-range helpers for the Reports page (pure, unit-tested). Dates are
// YYYY-MM-DD in the viewer's own time zone, which the API groups days by.

const pad = (n) => String(n).padStart(2, "0");

// ymd formats a Date as YYYY-MM-DD in local time.
export function ymd(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysAgo(now, n) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - n);
  return d;
}

// rangePresets are the quick picks beside the date range picker, each a
// [from, to] pair ending today (or a whole past month).
export function rangePresets(now = new Date()) {
  const today = ymd(now);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  return [
    { text: "Last 7 days", value: [ymd(daysAgo(now, 6)), today] },
    { text: "Last 30 days", value: [ymd(daysAgo(now, 29)), today] },
    { text: "Last 90 days", value: [ymd(daysAgo(now, 89)), today] },
    { text: "This month", value: [ymd(monthStart), today] },
    { text: "Last month", value: [ymd(lastMonthStart), ymd(lastMonthEnd)] },
    { text: "Last 12 months", value: [ymd(daysAgo(now, 364)), today] },
  ];
}

// defaultRange is the last 30 days, today included.
export function defaultRange(now = new Date()) {
  return [ymd(daysAgo(now, 29)), ymd(now)];
}

// rangeLabel names a range for the cards' subtitles: a preset's name when it
// is one, else "Mar 1, 2026 – Mar 31, 2026".
export function rangeLabel(range, now = new Date()) {
  const preset = rangePresets(now).find((p) => p.value[0] === range[0] && p.value[1] === range[1]);
  if (preset) return preset.text;
  const fmt = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };
  return `${fmt(range[0])} – ${fmt(range[1])}`;
}

// conversionRate is submissions per view, as a percent with one decimal, or
// "—" with no views to divide by.
export function conversionRate(submissions, views) {
  if (!views) return "—";
  return `${((100 * (submissions || 0)) / views).toFixed(1)}%`;
}

// shortDay labels a chart tick: "Mar 9".
export function shortDay(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// viewerTimeZone is the browser's IANA time zone ("" if it can't say).
export function viewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
}

// The device kinds the reports split by, in column order.
export const DEVICE_KINDS = [
  { key: "phone", label: "Phone" },
  { key: "tablet", label: "Tablet" },
  { key: "desktop", label: "Desktop" },
  { key: "unknown", label: "Unknown" },
];

// deviceQuery is the devices request value for the picked kinds: "" (all
// devices) when none or every kind is picked.
export function deviceQuery(picked) {
  if (picked.length === 0 || picked.length === DEVICE_KINDS.length) return "";
  return DEVICE_KINDS.map((k) => k.key).filter((k) => picked.includes(k)).join(",");
}

// The activity numbers with no device (they happen on the server, not in
// someone's browser), so a device filter can't apply to them.
export const NO_DEVICE_METRICS = ["new_contacts", "signup_emails", "incentives_sent"];

const realDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "") && !isNaN(Date.parse(s));

// The filters live in the page's address, so they survive going to another page
// and back, and a filtered report can be bookmarked or shared.

// rangeFromParams reads the dates from the address, or the default range when
// they're missing or don't make sense.
export function rangeFromParams(params, now = new Date()) {
  const from = params.get("from");
  const to = params.get("to");
  return realDay(from) && realDay(to) && from <= to ? [from, to] : defaultRange(now);
}

// devicesFromParams reads the device kinds, ignoring any it doesn't know.
export function devicesFromParams(params) {
  const picked = (params.get("devices") || "").split(",");
  return DEVICE_KINDS.map((k) => k.key).filter((k) => picked.includes(k));
}

// reportParams is the address's query for these filters. The defaults are left
// out, so an unfiltered report keeps a plain address and always means the
// latest 30 days.
export function reportParams(range, devices, now = new Date()) {
  const params = new URLSearchParams();
  const fallback = defaultRange(now);
  if (range[0] !== fallback[0] || range[1] !== fallback[1]) {
    params.set("from", range[0]);
    params.set("to", range[1]);
  }
  const kinds = deviceQuery(devices);
  if (kinds) params.set("devices", kinds);
  return params;
}
